import { useMemo } from 'react'
import { useDoors, useLocationTree } from '../../api/hooks'
import type { LocationNode } from '../../api/types'

function flattenLocations(nodes: LocationNode[], depth = 0): { uid: string; label: string; barrier: boolean }[] {
  return nodes.flatMap((n) => [
    {
      uid: n.unique_id,
      label: `${'— '.repeat(depth)}${n.name}${n.inherits_grants === false ? ' 🔒' : ''}`,
      barrier: n.inherits_grants === false,
    },
    ...flattenLocations(n.children, depth + 1),
  ])
}

/** The union scope picker shared by the door-code forms (static code,
 *  series, mapping decide): a location multi-select (the tree, flattened
 *  with depth) + a door multi-select; at least one pick required. On a
 *  static code this resolves ONE-SHOT backend-side; on a series the union
 *  IS the stored scope; on a door_code decide it lands as
 *  fields.locations/fields.doors. */
export function ScopePicker({
  locations,
  doors,
  onLocations,
  onDoors,
}: {
  locations: string[]
  doors: string[]
  onLocations: (uids: string[]) => void
  onDoors: (uids: string[]) => void
}) {
  const locationTree = useLocationTree()
  const doorList = useDoors()
  const flat = useMemo(() => flattenLocations(locationTree.data ?? []), [locationTree.data])

  const toggle = (list: string[], uid: string, set: (uids: string[]) => void) =>
    set(list.includes(uid) ? list.filter((u) => u !== uid) : [...list, uid])

  const boxClass =
    'flex max-h-40 flex-col gap-0.5 overflow-y-auto rounded-sm border border-ostrel-line bg-ostrel-card px-2 py-1.5'
  const rowClass = 'flex items-center gap-2 rounded-sm px-1.5 py-1 text-xs text-ostrel-ink hover:bg-ostrel-sand'

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
        Scope — locations and/or doors (at least one)
      </span>
      <div className="grid gap-2 md:grid-cols-2">
        <div className={boxClass} aria-label="Scope locations">
          {flat.length === 0 ? (
            <p className="px-1.5 py-1 text-[11px] text-ostrel-ink-muted">No locations.</p>
          ) : (
            flat.map((l) => (
              <label
                key={l.uid}
                title={l.barrier ? "Requires direct assignment — grants above don't inherit in" : undefined}
                className={rowClass}
              >
                <input
                  type="checkbox"
                  checked={locations.includes(l.uid)}
                  onChange={() => toggle(locations, l.uid, onLocations)}
                  className="accent-ostrel-gold"
                />
                <span className="tnum min-w-0 truncate">{l.label}</span>
              </label>
            ))
          )}
        </div>
        <div className={boxClass} aria-label="Scope doors">
          {(doorList.data ?? []).length === 0 ? (
            <p className="px-1.5 py-1 text-[11px] text-ostrel-ink-muted">No doors.</p>
          ) : (
            (doorList.data ?? []).map((d) => (
              <label key={d.unique_id} className={rowClass}>
                <input
                  type="checkbox"
                  checked={doors.includes(d.unique_id)}
                  onChange={() => toggle(doors, d.unique_id, onDoors)}
                  className="accent-ostrel-gold"
                />
                <span className="min-w-0 truncate">
                  {d.name}
                  <span className="text-ostrel-ink-muted"> — {d.breadcrumb.join(' / ') || d.location}</span>
                </span>
              </label>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
